import React from 'react';
import { BiSolidHotel, BiRestaurant, BiUser } from 'react-icons/bi';
import { MdOutlineInventory, MdQrCodeScanner, MdEventAvailable, MdAnalytics, MdCloud } from 'react-icons/md';
import { FaCheckCircle, FaArrowRight } from 'react-icons/fa';
import SEO from '../components/SEO';

const featuresData = [
  {
    title: 'Hotel Booking & Room Management',
    icon: <BiSolidHotel className="text-5xl" />,
    description: 'Complete hotel management with real-time room availability, booking system, and housekeeping coordination.',
    benefits: [
      'Real-time availability calendar prevents double bookings',
      'Instant room status updates for housekeeping',
      'Online travel agency (OTA) integration',
      'Automated booking confirmations and reminders',
      'Guest check-in/check-out management',
      'Room maintenance and cleaning tracking',
    ],
  },
  {
    title: 'Restaurant & POS System',
    icon: <BiRestaurant className="text-5xl" />,
    description: 'Integrated point-of-sale system with table management, kitchen display, and billing automation.',
    benefits: [
      'Digital order-taking directly to kitchen display',
      'Quick billing with split checks and multiple payments',
      'Real-time sales analytics and reporting',
      'Table management and reservation system',
      'Menu management with seasonal pricing',
      'Kitchen order tracking and fulfillment',
    ],
  },
  {
    title: 'Staff & Roster Management',
    icon: <BiUser className="text-5xl" />,
    description: 'Streamline employee scheduling, attendance tracking, payroll management, and task assignment.',
    benefits: [
      'Drag-and-drop roster creation and shift scheduling',
      'Digital attendance with check-in/out tracking',
      'Automated payroll calculations',
      'Centralized staff communication hub',
      'Task assignment and accountability tracking',
      'Leave management and approval workflow',
    ],
  },
  {
    title: 'Inventory & Stock Control',
    icon: <MdOutlineInventory className="text-5xl" />,
    description: 'Smart inventory management with low-stock alerts, supplier tracking, and consumption analytics.',
    benefits: [
      'Automated low-stock alerts and notifications',
      'Real-time inventory tracking across departments',
      'Purchase order creation and supplier management',
      'Consumption pattern analysis and insights',
      'Waste reduction and cost optimization',
      'Multi-location inventory synchronization',
    ],
  },
  {
    title: 'QR Menu & Contactless Ordering',
    icon: <MdQrCodeScanner className="text-5xl" />,
    description: 'Digital QR code menus for contactless ordering with direct kitchen integration and real-time updates.',
    benefits: [
      'Scan-to-order contactless menu system',
      'Real-time menu updates and availability',
      'Multi-language menu support',
      'Direct kitchen order integration',
      'Customer order history and preferences',
      'Promotional offers and special deals',
    ],
  },
  {
    title: 'Hotel Automation & Analytics',
    icon: <MdEventAvailable className="text-5xl" />,
    description: 'Automated workflows for hotel operations with comprehensive analytics and business insights.',
    benefits: [
      'Automated check-in and check-out processes',
      'Smart room assignment and optimization',
      'Revenue management and pricing strategies',
      'Comprehensive business analytics dashboard',
      'Predictive insights for demand forecasting',
      'Performance metrics and KPI tracking',
    ],
  },
  {
    title: 'Advanced Analytics & Reporting',
    icon: <MdAnalytics className="text-5xl" />,
    description: 'Powerful analytics tools to track performance, identify trends, and make data-driven decisions.',
    benefits: [
      'Real-time sales and revenue tracking',
      'Customer behavior and preference analysis',
      'Peak hours and demand pattern insights',
      'Staff performance and productivity metrics',
      'Inventory turnover and cost analysis',
      'Custom reports and data exports',
    ],
  },
  {
    title: 'Cloud-Based & Secure',
    icon: <MdCloud className="text-5xl" />,
    description: '24/7 cloud access with enterprise-grade security, automatic backups, and multi-device support.',
    benefits: [
      'Access from anywhere, anytime on any device',
      'Enterprise-grade data encryption and security',
      'Automatic daily backups and disaster recovery',
      'Multi-location management from single dashboard',
      '99.9% uptime guarantee with 24/7 support',
      'Regular software updates and new features',
    ],
  },
];

const FeatureCard = ({ feature, index }) => {
  return (
    <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-transparent hover:border-[#4299f5] transform hover:-translate-y-1">
      {/* Icon */}
      <div className="flex justify-center mb-6">
        <div className="w-24 h-24 bg-gradient-to-br from-[#4299f5] to-[#2d7dd1] rounded-full flex items-center justify-center shadow-lg text-white">
          {feature.icon}
        </div>
      </div>
      
      {/* Title */}
      <h3 className="text-2xl font-bold text-center mb-4 text-gray-900" style={{fontFamily: 'Montserrat, sans-serif'}}>
        {feature.title}
      </h3>
      
      {/* Description */}
      <p className="text-gray-600 text-center mb-6 leading-relaxed">
        {feature.description}
      </p>
      
      {/* Benefits List */}
      <div className="space-y-3">
        {feature.benefits.map((benefit, i) => (
          <div key={i} className="flex items-start gap-3">
            <FaCheckCircle className="text-green-500 flex-shrink-0 mt-1" />
            <span className="text-gray-700 text-sm">{benefit}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const Features = () => {
  return (
    <div className="bg-gray-50">
      <SEO 
        title="Features - Comprehensive Hotel & Restaurant Management"
        description="Explore Trivana's powerful features: Room management, Table booking, QR menu ordering, POS billing, Inventory tracking, Staff management, Real-time analytics, and automated workflows for hotels and restaurants."
        keywords="hotel management features, restaurant POS, QR menu, room booking system, table reservation, inventory management, staff attendance, automated billing, hospitality analytics"
        url="/features"
      />
      
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-black overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
          <div className="text-center animate-fade-in-up">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-white mb-6" style={{fontFamily: 'Montserrat, sans-serif'}}>
              Powerful Features for<br />
              <span className="text-[#4299f5]">Modern Hospitality</span>
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
              Everything you need to run your hotel, restaurant, or cafe efficiently — all in one comprehensive platform
            </p>
          </div>
        </div>
        {/* Decorative elements */}
        <div className="absolute top-20 left-10 w-40 h-40 bg-[#4299f5]/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-60 h-60 bg-[#4299f5]/20 rounded-full blur-3xl"></div>
      </div>

      {/* Features Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {featuresData.map((feature, index) => (
            <FeatureCard key={index} feature={feature} index={index} />
          ))}
        </div>
      </div>

      {/* CTA Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="bg-gradient-to-r from-[#4299f5] to-[#2d7dd1] rounded-2xl p-12 shadow-2xl text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-white" style={{fontFamily: 'Montserrat, sans-serif'}}>
            Ready to Transform Your Business?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Start your 7-day free trial and experience all features with no credit card required
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/pricing"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 sm:px-8 sm:py-4 bg-white text-[#4299f5] text-base sm:text-lg font-bold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              style={{fontFamily: 'Montserrat, sans-serif'}}
            >
              View Pricing Plans
              <FaArrowRight />
            </a>
            <a
              href="/contact"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 sm:px-8 sm:py-4 bg-[#2d7dd1] hover:bg-[#1e5fa8] text-white text-base sm:text-lg font-bold rounded-full shadow-lg transition-all duration-300 transform hover:scale-105"
              style={{fontFamily: 'Montserrat, sans-serif'}}
            >
              Talk to Sales
              <FaArrowRight />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Features;
